import React from 'react';
import { useApp } from '../context/AppContext';

export default function GettingStarted({ product, setActivePage }) {
  const { baseUrl, currentEnv } = useApp();
  const gs = product?.gettingStarted || {};
  const steps = gs.steps || [];
  const endpointCards = gs.endpointCards || [];
  const authItems = gs.authItems || [];
  const displayBase = baseUrl || 'Set API environment in header';

  return (
    <div className="page-content">

      <div style={{ marginBottom: 48, position: 'relative' }}>
        <div style={{
          position: 'absolute', top: -20, left: -20, right: -20, height: 200,
          background: 'radial-gradient(ellipse at 30% 50%, var(--accent-bg) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 12px', borderRadius: 20,
          background: 'var(--accent-bg)', border: '1px solid var(--accent-glow)',
          fontSize: 12, fontWeight: 600, color: 'var(--accent)',
          marginBottom: 16, letterSpacing: '0.04em',
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 6px var(--accent)' }} />
          API Documentation v1.0
        </div>
        <h1 className="page-title page-title--hero">
          {gs.title}<br />
          <span style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {gs.subtitle || 'API Reference'}
          </span>
        </h1>
        <p style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.75, maxWidth: 560, marginBottom: 28 }}>
          {product?.description}
        </p>

        <div className="base-url-pill">
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Base URL</span>
          <code style={{ fontSize: 13.5, color: 'var(--accent-light)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 500 }}>
            {displayBase}
          </code>
          <div style={{ width: 1, height: 14, background: 'var(--border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: currentEnv.color, fontWeight: 500 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: currentEnv.color, boxShadow: `0 0 5px ${currentEnv.color}` }} />
            {currentEnv.label}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20, letterSpacing: '-0.02em' }}>
          Quick Start
        </h2>
        <div className="grid-2">
          {steps.map((s, i) => (
            <div key={i} style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 14, padding: 20,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                background: `linear-gradient(90deg, ${s.color}, ${s.color}88)`,
              }} />
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11, fontWeight: 700, color: s.color,
                background: `${s.color}12`, padding: '3px 10px', borderRadius: 20,
                border: `1px solid ${s.color}25`, marginBottom: 10,
                fontFamily: 'JetBrains Mono, monospace',
              }}>STEP {s.num}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, letterSpacing: '-0.01em' }}>{s.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20, letterSpacing: '-0.02em' }}>
          All Endpoints
        </h2>
        <div className="grid-3">
          {endpointCards.map(card => (
            <div
              key={card.id}
              onClick={() => setActivePage(card.id)}
              style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '16px 18px',
                cursor: 'pointer', position: 'relative', overflow: 'hidden',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = card.color + '50';
                e.currentTarget.style.background = 'var(--bg-elevated)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--bg-surface)';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 8 }}>{card.emoji}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{card.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{card.desc}</div>
              <div style={{ position: 'absolute', top: 14, right: 14 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
                  background: 'var(--post-bg)', color: 'var(--post)',
                  border: '1px solid var(--post-border)',
                  fontFamily: 'JetBrains Mono, monospace',
                }}>POST</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 24,
      }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, letterSpacing: '-0.01em' }}>
          Authentication Overview
        </h2>
        <div className="grid-2" style={{ gap: 12 }}>
          {authItems.map((item, i) => (
            <div key={i} style={{
              background: 'var(--bg-elevated)', borderRadius: 10, padding: '12px 14px',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${item.color}`,
              minWidth: 0,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 500 }}>{item.label}</div>
              <code style={{ fontSize: 12.5, color: 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-word' }}>{item.val}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
